import { Chakra, Logos } from "@airhelp/react-ui-components";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { useLocalizedData } from "contexts/LocalizationContext/hooks";
import { AHPLUS_PRICE } from "modules/storyblok/components/AhPlusPricingPlans/config";
import { ArrowRightIcon } from "styles/icons";
import type { StickyBottomBarIconProps, StickyBottomBarProps } from "../StickyBottomBar.d";
import { stickyBottomBarDesktopStyles } from "./StickyBottomBarDesktop.styles";
import { getCTAElementProps } from "../utils/getCTAElementProps";

const { Flex, Button, Heading, HStack, Text, Box } = Chakra;

const StickyBottomBarDesktop = ({
  iconsWithText,
  heading,
  variant,
}: {
  iconsWithText: StickyBottomBarIconProps[];
  heading: StickyBottomBarProps["heading"];
  variant: StickyBottomBarProps["variant"];
}) => {
  const language = useLanguage();
  const translations = useTranslations();
  const { getCompensationValue, getLocalizedCurrency } = useLocalizedData();
  const CTAButtonProps = getCTAElementProps(language, variant);

  const customHeading = {
    basic: heading || translations("claim_up_to_compensation", { "%compensation%": getCompensationValue() }),
    ahPlus: heading || translations("be_prepared_for_future_flight_disruptions"),
  }[variant];

  const buttonText = {
    basic: translations("check_compensation"),
    ahPlus: translations("from_commission_value_per-year", {
      "%commission_value%": getLocalizedCurrency(AHPLUS_PRICE, "EUR", 2),
    }),
  }[variant] as string;

  return (
    <Box
      p={{ lg: 6 }}
      color="greyscale.100"
      borderRadius="lg"
      mb={8}
      maxW="1202px"
      mx={{ lg: 4, xl: "auto" }}
      sx={stickyBottomBarDesktopStyles[variant].container}
      maxH={40}
    >
      <Flex sx={stickyBottomBarDesktopStyles[variant].innerContainer}>
        <Logos.AirhelpPlus sx={stickyBottomBarDesktopStyles[variant].logo} />
        <Box sx={stickyBottomBarDesktopStyles[variant].headingContainer}>
          <Heading as="h4" variant="h4" sx={stickyBottomBarDesktopStyles[variant].heading}>
            {customHeading}
          </Heading>
        </Box>
        <Flex justify="space-between" sx={stickyBottomBarDesktopStyles[variant].innerSubContainer}>
          <HStack spacing={10} sx={stickyBottomBarDesktopStyles[variant].iconsWithText}>
            {iconsWithText.map(({ textKey, icon: Icon }) => (
              <Flex align="center" key={textKey}>
                <Flex borderRadius="50%" border="2px solid" borderColor="primary.400" mr={2} as="span" p={2}>
                  <Icon boxSize={5} />
                </Flex>
                <Text color="white" as="span" fontSize="sm">
                  {translations(textKey)}
                </Text>
              </Flex>
            ))}
          </HStack>
          <Button
            as="a"
            variant="subtle"
            sx={{ svg: { boxSize: 4 }, ...stickyBottomBarDesktopStyles[variant].actionButton }}
            {...CTAButtonProps}
          >
            {buttonText} <ArrowRightIcon ml={3} />
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default StickyBottomBarDesktop;
